import { useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';

import { formatLocalDate } from '@iconik-io/core/src/date/format';
import {
  httpPostSearch,
  type SearchSearchCriteriaSchema,
} from '@iconik-io/iconik-api/src/services/search';
import { ScrubbableThumbnail } from '@iconik-io/shared-lib/src/react/components/shared/scrubbable_thumbnail/scrubbable_thumbnail';
import { AssetTypeIcon } from '@iconik-io/shared-lib/src/react/components/shared/ui/asset_type_icon/asset_type_icon';
import { useDebounce } from '@iconik-io/shared-lib/src/react/hooks/use_debounce';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { prepareQueryForMainSearch } from '@iconik-io/shared-lib/src/utils/search/prepare_query_for_main_search';
import { selectionToFirstStringItem } from '@iconik-io/shared-lib/src/utils/selection_to_first_string_item';
import { selectionToSet } from '@iconik-io/shared-lib/src/utils/selection_to_set';
import {
  ConfirmDialog,
  EmptyState,
  ListItem,
  ListView,
  ProgressCircle,
  SearchField,
} from '@iconik-io/uad';

import { INPUT_DEBOUNCE_TIMEOUT } from '../../../../constants/debounce';
import { filtersObjectToElastic } from '../../../../ducks/pages/search/utils';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../store/react_redux_typed';

import styles from './select_thumbnail_asset_modal.module.css';

const doSearch = (query: string, excludeId?: string) => {
  const searchDocument: SearchSearchCriteriaSchema = {
    query: prepareQueryForMainSearch(query),
    doc_types: ['assets'],
    include_fields: [
      'id',
      'title',
      'type',
      'object_type',
      'formats',
      'keyframes',
      'files',
      'versions',
      'proxies',
    ],
    facets_filters: [
      {
        name: 'media_type',
        value_in: ['image'],
      },
    ],
    filter: {
      filters: [
        {
          operator: 'NOT',
          terms: filtersObjectToElastic({
            id: excludeId,
            status: 'DELETED',
          }),
        },
      ],
      operator: 'AND',
      terms: [{ name: 'type', value_in: ['ASSET'] }],
    },
    sort: [{ name: 'date_created', order: 'desc' }],
  };
  return httpPostSearch(searchDocument, {
    per_page: 20,
    save_search_history: false,
  });
};

type Props = {
  currentAssetId: string;
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (searchItem: SearchItemEntity) => void;
};

export function SelectThumbnailAssetModal(props: Props) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const dateTimeFormat = useAppSelector(
    (state) => state.settings.merged.datetime_format,
  );

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchItems, setSearchItems] = useState<SearchItemEntity[]>([]);
  const [totalItems, setTotalItems] = useState<number | undefined>(undefined);
  const [selectedSearchItem, setSelectedSearchItem] = useState<
    SearchItemEntity | undefined
  >(undefined);

  const dispatch = useAppDispatch();

  const search = useCallback(
    (queryValue: string): void => {
      setLoading(true);
      setTotalItems(undefined);
      dispatch(doSearch(queryValue, props.currentAssetId))
        .then((response) => {
          const { objects: newObjectsValue } = response.body;

          setTotalItems(response.body.total);
          // Elastic schemas straight from search, held as the normalized
          // entity the list components read. See asset_relations for the same.
          // biome-ignore lint/plugin: see above.
          setSearchItems(newObjectsValue as SearchItemEntity[]);
        })
        .finally(() => setLoading(false));
    },
    [props.currentAssetId],
  );

  useEffect(() => {
    if (!props.isOpen) {
      return;
    }
    setQuery('');
    setTotalItems(undefined);
    setLoading(false);
    setSelectedIds(new Set());
    setSearchItems([]);
    setSelectedSearchItem(undefined);

    search('');
  }, [props.isOpen, search]);

  const searchDebounced = useDebounce(search, INPUT_DEBOUNCE_TIMEOUT, [query]);

  const handleQueryUpdate = (queryValue: string): void => {
    setSelectedIds(new Set());
    setSelectedSearchItem(undefined);
    setQuery(queryValue);

    searchDebounced(queryValue);
  };

  return (
    <ConfirmDialog
      isOpen={props.isOpen}
      title="Select thumbnail"
      onCancel={props.onCancel}
      cancelButtonLabel="Cancel"
      confirmButtonLabel="Use thumbnail"
      isConfirmDisabled={!selectedSearchItem}
      onConfirm={() => {
        if (selectedSearchItem) {
          props.onConfirm(selectedSearchItem);
        }
      }}
      size="m"
    >
      <div className={styles.dialog_content}>
        <SearchField
          placeholder="Search thumbnail..."
          aria-label="Search thumbnails"
          size="l"
          autoFocus
          value={query}
          onChange={handleQueryUpdate}
        />

        <div
          className={classNames(styles.total_items, {
            [styles.total_items_hidden]: totalItems === undefined,
          })}
        >
          {totalItems} Assets
        </div>

        <div className={styles.assets_list}>
          {loading ? (
            <div className={styles.loading_wrapper}>
              <ProgressCircle size="xl" />
            </div>
          ) : null}
          {!loading && searchItems.length > 0 ? (
            <ListView
              aria-label="Image assets list"
              gap="s"
              items={searchItems}
              selectedKeys={selectedIds}
              onSelectionChange={(selection) => {
                setSelectedIds(selectionToSet(selection));
                const firstId = selectionToFirstStringItem(selection);
                if (!firstId) {
                  setSelectedSearchItem(undefined);
                  return;
                }

                const firstSelectedSearchItem = searchItems.find(
                  (searchItem) => searchItem.id === firstId,
                );
                setSelectedSearchItem(firstSelectedSearchItem);
              }}
              selectionBehavior="replace"
              selectionMode="single"
            >
              {(searchItem) => {
                return (
                  <ListItem
                    className={styles.item}
                    textValue={searchItem.title}
                  >
                    <div className={styles.thumbnail_wrapper}>
                      <ScrubbableThumbnail
                        searchItem={searchItem}
                        keyframes={searchItem.keyframes ?? undefined}
                        size="l"
                        cardSize="m"
                        rounded
                      />
                    </div>
                    <div className={styles.item_content}>
                      <div className={styles.item_icon_title_wrapper}>
                        <AssetTypeIcon searchItem={searchItem} />
                        <span className={styles.item_title}>
                          {searchItem.title}
                        </span>
                      </div>
                      <div className={styles.item_date}>
                        {formatLocalDate(
                          searchItem.date_created ?? undefined,
                          dateTimeFormat ?? undefined,
                        )}
                      </div>
                    </div>
                  </ListItem>
                );
              }}
            </ListView>
          ) : null}
          {!loading && searchItems.length === 0 ? (
            <EmptyState title="No results found" iconName="search" />
          ) : null}
        </div>
      </div>
    </ConfirmDialog>
  );
}
